import { useState, useCallback } from 'react';
import { HomeSection } from './useHomeSections';

// Backup system for critical sections to prevent accidental loss
interface SectionBackup {
  timestamp: string;
  sections: HomeSection[];
  description: string;
}

const CRITICAL_SECTIONS = ['hero', 'about-sectors', 'about'];

export const useSectionBackup = () => {
  const [backups, setBackups] = useState<SectionBackup[]>([]);

  // Create backup of current critical sections
  const createBackup = useCallback((sections: HomeSection[], description: string = 'Auto backup') => {
    const criticalSections = sections.filter(section => 
      CRITICAL_SECTIONS.includes(section.section_key)
    );

    const backup: SectionBackup = {
      timestamp: new Date().toISOString(),
      sections: criticalSections,
      description
    };

    setBackups(prev => [backup, ...prev.slice(0, 4)]); // Keep last 5 backups
    
    // Also save to localStorage for persistence
    const storedBackups = JSON.parse(localStorage.getItem('abask-section-backups') || '[]');
    const updatedBackups = [backup, ...storedBackups.slice(0, 4)];
    localStorage.setItem('abask-section-backups', JSON.stringify(updatedBackups));

    console.log('🔐 Section backup created:', description);
    return backup;
  }, []);

  // Restore from backup
  const restoreFromBackup = useCallback((backupIndex: number): HomeSection[] | null => {
    const storedBackups = JSON.parse(localStorage.getItem('abask-section-backups') || '[]');
    const backup = storedBackups[backupIndex];
    
    if (!backup) {
      console.error('❌ Backup not found');
      return null;
    }

    console.log('🔄 Restoring sections from backup:', backup.description);
    return backup.sections;
  }, []);

  // Get latest backup
  const getLatestBackup = useCallback((): SectionBackup | null => {
    const storedBackups = JSON.parse(localStorage.getItem('abask-section-backups') || '[]');
    return storedBackups[0] || null;
  }, []);

  // Auto-backup on critical changes with deduplication
  const autoBackup = useCallback((sections: HomeSection[]) => {
    const hasCriticalSections = sections.some(section => 
      CRITICAL_SECTIONS.includes(section.section_key)
    );

    if (!hasCriticalSections) return;

    // Create a hash of the critical sections to prevent duplicate backups
    const criticalSections = sections.filter(section => 
      CRITICAL_SECTIONS.includes(section.section_key)
    );
    const sectionsHash = JSON.stringify(criticalSections.map(s => ({
      id: s.id,
      section_key: s.section_key,
      title_ar: s.title_ar,
      title_en: s.title_en,
      updated_at: s.updated_at
    })));

    // Check if this is different from the last backup
    const storedBackups = JSON.parse(localStorage.getItem('abask-section-backups') || '[]');
    const lastBackup = storedBackups[0];
    
    if (lastBackup) {
      const lastHash = JSON.stringify(lastBackup.sections.map((s: any) => ({
        id: s.id,
        section_key: s.section_key,
        title_ar: s.title_ar,
        title_en: s.title_en,
        updated_at: s.updated_at
      })));
      
      if (sectionsHash === lastHash) {
        return; // No changes, skip backup
      }
    }

    createBackup(sections, 'Auto backup - Critical sections updated');
  }, [createBackup]);

  return {
    createBackup,
    restoreFromBackup,
    getLatestBackup,
    autoBackup,
    backups
  };
};